import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../../component-lib/Atoms/Input/Input";
import Button from "../../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import PatientListService from "../../../../services/PatientListService";
import { eventEHRCustomToastMessage } from "../../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    width: "520px",
    maxWidth: "none",
  },
}));

const FIELD = {
  TYPE: "type",
  CODE_NAME: "codeName",
  DIAGNOSIS_NAME: "diagnosisName",
  DESCRIPTION: "description",
};

interface CodeDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  onCreated: (newCode: any) => void;
  defaultCode?: string;
}

export default function CodeDialog({
  isOpen,
  handleClose,
  onCreated,
  defaultCode = "",
}: CodeDialogProps) {
  const validationSchema = Yup.object().shape({
    [FIELD.TYPE]: Yup.string()
      .oneOf(["ICD10", "DSM"])
      .required("Type is required"),
    [FIELD.CODE_NAME]: Yup.string()
      .max(20, "Code should not exceed 20 characters")
      .required("Code is required"),
    [FIELD.DESCRIPTION]: Yup.string().required("Description is required"),
    [FIELD.DIAGNOSIS_NAME]: Yup.string().when(FIELD.TYPE, {
      is: "ICD10",
      then: (schema) => schema.required("Diagnosis Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const payload: any = {
        type: values[FIELD.TYPE],
        code: values[FIELD.CODE_NAME],
        description: values[FIELD.DESCRIPTION],
      };
      if (values[FIELD.DIAGNOSIS_NAME]) {
        payload.diagnosisName = values[FIELD.DIAGNOSIS_NAME];
      }
      const response = await PatientListService.postMasterCode(payload);
      const newCode = { ...payload, ...(response?.data?.data ?? {}) };
      eventEHRCustomToastMessage({
        status: "success",
        title: `${values[FIELD.TYPE]} code created`,
      });
      onCreated(newCode);
    } catch (error: any) {
      eventEHRCustomToastMessage({
        status: "error",
        title:
          error?.response?.data?.message ||
          "Failed to create code. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="code-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [FIELD.TYPE]: "ICD10",
          [FIELD.CODE_NAME]: defaultCode,
          [FIELD.DIAGNOSIS_NAME]: "",
          [FIELD.DESCRIPTION]: "",
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => {
          const isICD10 = values[FIELD.TYPE] === "ICD10";
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="code-dialog-title"
              >
                Add New Code
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-2">
                    <div
                      className="font-weight-500 font-size-14px color-212D30 mb-2"
                      style={{ fontFamily: "Inter" }}
                    >
                      Code Type
                    </div>
                    <RadioGroup
                      row
                      name={FIELD.TYPE}
                      value={values[FIELD.TYPE]}
                      onChange={(e) =>
                        setFieldValue(FIELD.TYPE, e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="ICD10"
                        control={<Radio size="small" />}
                        label="ICD-10"
                      />
                      <FormControlLabel
                        value="DSM"
                        control={<Radio size="small" />}
                        label="DSM"
                      />
                    </RadioGroup>
                  </div>

                  <Input
                    textFieldLabel={isICD10 ? "ICD-10 Code" : "DSM Code"}
                    maxLength={20}
                    size="small"
                    name={FIELD.CODE_NAME}
                    value={values[FIELD.CODE_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(FIELD.CODE_NAME, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[FIELD.CODE_NAME] &&
                      Boolean(errors[FIELD.CODE_NAME])
                    }
                    helperText={
                      touched[FIELD.CODE_NAME]
                        ? (errors[FIELD.CODE_NAME] as string)
                        : undefined
                    }
                    // isStarPresent
                  />

                  <Input
                    textFieldLabel="Diagnosis Name"
                    size="small"
                    name={FIELD.DIAGNOSIS_NAME}
                    value={values[FIELD.DIAGNOSIS_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(FIELD.DIAGNOSIS_NAME, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[FIELD.DIAGNOSIS_NAME] &&
                      Boolean(errors[FIELD.DIAGNOSIS_NAME])
                    }
                    helperText={
                      touched[FIELD.DIAGNOSIS_NAME]
                        ? (errors[FIELD.DIAGNOSIS_NAME] as string)
                        : undefined
                    }
                    // isStarPresent={isICD10}
                  />

                  <Input
                    textFieldLabel="Description"
                    multiline
                    maxWords={250}
                    rows={4}
                    size="small"
                    name={FIELD.DESCRIPTION}
                    value={values[FIELD.DESCRIPTION]}
                    onChangeCb={(e) => {
                      setFieldValue(FIELD.DESCRIPTION, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[FIELD.DESCRIPTION] &&
                      Boolean(errors[FIELD.DESCRIPTION])
                    }
                    helperText={
                      touched[FIELD.DESCRIPTION]
                        ? (errors[FIELD.DESCRIPTION] as string)
                        : undefined
                    }
                    // isStarPresent
                  />
                </div>
              </DialogContent>

              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "160px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
